import { type ReactNode, useCallback, useRef } from 'react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { Separator } from '@nesso/ui/primitives/separator';

import { SearchableCheckboxList } from './searchable-checkbox-list';
import { SelectedBadgeList } from './selected-badge-list';

interface Props {
    options: { value: string; label: string; group?: string; keywords?: string[] }[];
    selected: string[];
    onChange: (selected: string[]) => void;
    maxSelected?: number;
    searchPlaceholder?: string;
    notFoundMessage?: string;
    deleteBadgeIcon?: ReactNode;
    disabled?: boolean;
    loading?: boolean;
    groupOrder?: string[];
    badgeClassName?: string;
}

export function InlineMultiSelect({
    options,
    selected,
    onChange,
    maxSelected,
    searchPlaceholder = 'Search',
    notFoundMessage = 'No options found.',
    loading = false,
    disabled = false,
    deleteBadgeIcon,
    groupOrder,
    badgeClassName,
}: Props) {
    const isSm = useBreakpointDown('sm');

    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const handleRemove = useCallback((value: string) => {
        const current = selectedRef.current;
        onChangeRef.current(current.filter((v) => v !== value));
    }, []);

    return (
        <div className="flex flex-col h-full overflow-hidden pt-0 sm:gap-3">
            <div className="flex flex-col">
                <SelectedBadgeList
                    options={options}
                    selected={selected}
                    onRemove={handleRemove}
                    deleteIcon={deleteBadgeIcon}
                    disabled={disabled}
                    className="px-4 pb-4"
                    badgeClassName={badgeClassName}
                />
                <Separator />
            </div>
            <SearchableCheckboxList
                options={options}
                selected={selected}
                onChange={onChange}
                maxSelected={maxSelected}
                searchPlaceholder={searchPlaceholder}
                notFoundMessage={notFoundMessage}
                disabled={disabled || loading}
                inputLast={isSm}
                className="gap-3"
                inputWrapperClassName="sm:max-w-[23.75rem]"
                emptyWrapperClassName="sm:max-w-[23.75rem]"
                commandGroupClassName="pl-1 pr-3.5 sm:pl-0 sm:pr-0 sm:max-w-[23.75rem]"
                commandListClassName="max-h-none"
                groupOrder={groupOrder}
            />
        </div>
    );
}
